import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as checklistsApi from './api';
import type {
    CreateTemplatePayload,
    UpdateTemplatePayload,
    AssignChecklistPayload,
    BulkAssignPayload,
    CreateRecurringSchedulePayload,
    ReassignStepPayload,
} from './types';
import { toast } from 'sonner';

const KEYS = {
    templates: ['checklist-templates'] as const,
    templateDetail: (id: string) => ['checklist-templates', id] as const,
    checklists: ['checklists'] as const,
    checklistList: (params?: Record<string, unknown>) =>
        [...KEYS.checklists, 'list', params] as const,
    checklistDetail: (id: string) => [...KEYS.checklists, 'detail', id] as const,
    summary: ['checklist-summary'] as const,
    recurring: ['checklist-recurring'] as const,
    completionReport: ['checklist-completion-report'] as const,
    bottleneckReport: ['checklist-bottleneck-report'] as const,
};

// ── Template Queries ─────────────────────────────────

export function useTemplates(category?: string) {
    return useQuery({
        queryKey: [...KEYS.templates, category],
        queryFn: () => checklistsApi.listTemplates(category),
    });
}

export function useTemplate(id: string) {
    return useQuery({
        queryKey: KEYS.templateDetail(id),
        queryFn: () => checklistsApi.getTemplate(id),
        enabled: !!id,
    });
}

export function useCreateTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: CreateTemplatePayload) =>
            checklistsApi.createTemplate(payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.templates });
            toast.success('Template created');
        },
        onError: () => toast.error('Failed to create template'),
    });
}

export function useDeactivateTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => checklistsApi.deactivateTemplate(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.templates });
            toast.success('Template deactivated');
        },
        onError: () => toast.error('Failed to deactivate template'),
    });
}

// ── Checklist Queries ────────────────────────────────

export function useChecklists(params?: {
    status?: string;
    template_id?: string;
    subject_user_id?: string;
    page?: number;
    per_page?: number;
}) {
    return useQuery({
        queryKey: KEYS.checklistList(params as Record<string, unknown>),
        queryFn: () => checklistsApi.listChecklists(params),
    });
}

export function useChecklist(id: string) {
    return useQuery({
        queryKey: KEYS.checklistDetail(id),
        queryFn: () => checklistsApi.getChecklist(id),
        enabled: !!id,
    });
}

export function useChecklistSummary() {
    return useQuery({
        queryKey: KEYS.summary,
        queryFn: checklistsApi.getSummary,
        refetchInterval: 60_000,
    });
}

// ── Checklist Mutations ──────────────────────────────

export function useAssignChecklist() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: AssignChecklistPayload) =>
            checklistsApi.assignChecklist(payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.checklists });
            qc.invalidateQueries({ queryKey: KEYS.summary });
            toast.success('Checklist assigned');
        },
        onError: () => toast.error('Failed to assign checklist'),
    });
}

export function useBulkAssign() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: BulkAssignPayload) =>
            checklistsApi.bulkAssign(payload),
        onSuccess: (result) => {
            qc.invalidateQueries({ queryKey: KEYS.checklists });
            qc.invalidateQueries({ queryKey: KEYS.summary });
            toast.success(`Assigned to ${result.assigned} people`);
        },
        onError: () => toast.error('Failed to bulk assign'),
    });
}

export function useCancelChecklist() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) => checklistsApi.cancelChecklist(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.checklists });
            qc.invalidateQueries({ queryKey: KEYS.summary });
            toast.success('Checklist cancelled');
        },
        onError: () => toast.error('Failed to cancel checklist'),
    });
}

// ── Step Mutations ───────────────────────────────────

export function useCompleteStep(checklistId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (stepId: string) =>
            checklistsApi.completeStep(checklistId, stepId),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.checklistDetail(checklistId) });
            qc.invalidateQueries({ queryKey: KEYS.summary });
            toast.success('Step completed');
        },
        onError: () => toast.error('Failed to complete step'),
    });
}

export function useBlockStep(checklistId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (params: { stepId: string; reason: string }) =>
            checklistsApi.blockStep(checklistId, params.stepId, params.reason),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.checklistDetail(checklistId) });
            toast.success('Step marked as blocked');
        },
        onError: () => toast.error('Failed to block step'),
    });
}

export function useUpdateStep(checklistId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (params: {
            stepId: string;
            payload: { status?: string; evidence_url?: string; notes?: string };
        }) => checklistsApi.updateStep(checklistId, params.stepId, params.payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.checklistDetail(checklistId) });
            toast.success('Step updated');
        },
        onError: () => toast.error('Failed to update step'),
    });
}

// -- User Search --

export function useSearchUsers(query: string, enabled = true) {
    return useQuery({
        queryKey: ['users', query],
        queryFn: () => checklistsApi.searchUsers(query),
        enabled: enabled && query.trim().length >= 2,
        staleTime: 30_000, // cache results for 30s
    });
}

// -- Template Update --

export function useUpdateTemplate() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (params: { id: string; payload: UpdateTemplatePayload }) =>
            checklistsApi.updateTemplate(params.id, params.payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.templates });
            toast.success('Template updated (new version created)');
        },
        onError: () => toast.error('Failed to update template'),
    });
}

// -- Step Actions --

export function useSkipStep(checklistId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (params: { stepId: string; reason: string }) =>
            checklistsApi.skipStep(checklistId, params.stepId, params.reason),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.checklistDetail(checklistId) });
            qc.invalidateQueries({ queryKey: KEYS.summary });
            toast.success('Step skipped');
        },
        onError: () => toast.error('Failed to skip step'),
    });
}

export function useReassignStep(checklistId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (params: { stepId: string; payload: ReassignStepPayload }) =>
            checklistsApi.reassignStep(checklistId, params.stepId, params.payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.checklistDetail(checklistId) });
            toast.success('Step reassigned');
        },
        onError: () => toast.error('Failed to reassign step'),
    });
}

export function useNudgeStep(checklistId: string) {
    return useMutation({
        mutationFn: (params: { stepId: string; message?: string }) =>
            checklistsApi.nudgeStep(checklistId, params.stepId, { message: params.message }),
        onSuccess: () => toast.success('Reminder sent'),
        onError: () => toast.error('Failed to send reminder'),
    });
}

// -- Recurring Schedules --

export function useRecurringSchedules() {
    return useQuery({
        queryKey: KEYS.recurring,
        queryFn: checklistsApi.listRecurringSchedules,
    });
}

export function useCreateRecurringSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (payload: CreateRecurringSchedulePayload) =>
            checklistsApi.createRecurringSchedule(payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.recurring });
            toast.success('Recurring schedule created');
        },
        onError: () => toast.error('Failed to create recurring schedule'),
    });
}

export function useDeactivateRecurringSchedule() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id: string) =>
            checklistsApi.deactivateRecurringSchedule(id),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: KEYS.recurring });
            toast.success('Recurring schedule deactivated');
        },
        onError: () => toast.error('Failed to deactivate schedule'),
    });
}

// -- Reports --

export function useCompletionReport() {
    return useQuery({
        queryKey: KEYS.completionReport,
        queryFn: checklistsApi.getCompletionReport,
    });
}

export function useBottleneckReport() {
    return useQuery({
        queryKey: KEYS.bottleneckReport,
        queryFn: checklistsApi.getBottleneckReport,
    });
}